/**
 * Cleaning up model-written text before a child reads it.
 *
 * Everything here is a boundary repair, not a feature. A model asked for JSON
 * sometimes writes the *source form* of a character instead of the character —
 * `\u00f7` where it meant `÷` — and JSON.parse has no reason to notice,
 * because the model escaped the backslash and produced a string that is exactly
 * what it asked for. It reaches the tablet as six literal characters.
 *
 * A prompt asking it not to helps and does not fix it, so this runs on every
 * string the model produces on the way in.
 */
/** Codepoints we refuse to decode into: C0 and C1 controls, plus DEL. */
function printable(code) {
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f))
        return false;
    return code <= 0x10ffff;
}
const HIGH_SURROGATE = /^[\ud800-\udbff]$/;
/**
 * Turns literal `\uXXXX` and `\u{XXXXX}` sequences into the characters they
 * spell. Anything else — including a genuinely escaped backslash — is left
 * exactly as it was found.
 *
 * The parity handling matters: `\\u00f7` is a backslash followed by the text
 * "u00f7", which is what you would write if you were *talking about* the escape
 * rather than using it. Consuming backslash pairs before looking for `u` keeps
 * that intact.
 */
export function decodeStrayEscapes(text) {
    if (!text.includes("\\u"))
        return text;
    let out = "";
    let i = 0;
    while (i < text.length) {
        if (text[i] !== "\\") {
            out += text[i];
            i += 1;
            continue;
        }
        // An escaped backslash: both characters are literal, and whatever follows
        // is not an escape sequence.
        if (text[i + 1] === "\\") {
            out += "\\\\";
            i += 2;
            continue;
        }
        const read = readEscape(text, i);
        if (!read) {
            out += text[i];
            i += 1;
            continue;
        }
        out += read.char;
        i += read.length;
    }
    return out;
}
/** One escape at `start`, or nothing if it isn't one we're willing to decode. */
function readEscape(text, start) {
    const braced = /^\\u\{([0-9a-fA-F]{1,6})\}/.exec(text.slice(start, start + 10));
    if (braced) {
        const code = parseInt(braced[1], 16);
        if (!printable(code) || (code >= 0xd800 && code <= 0xdfff))
            return undefined;
        return { char: String.fromCodePoint(code), length: braced[0].length };
    }
    const plain = /^\\u([0-9a-fA-F]{4})/.exec(text.slice(start, start + 6));
    if (!plain)
        return undefined;
    const code = parseInt(plain[1], 16);
    if (!printable(code))
        return undefined;
    const char = String.fromCharCode(code);
    if (!HIGH_SURROGATE.test(char)) {
        // A low surrogate on its own would corrupt the string it lands in.
        if (code >= 0xdc00 && code <= 0xdfff)
            return undefined;
        return { char, length: plain[0].length };
    }
    /*
      Emoji arrive as a surrogate pair — `\ud83d\ude00` is one smiling face.
      Half of a pair is not a character, so both halves are decoded together or
      neither is touched.
    */
    const low = /^\\u([dD][c-fC-F][0-9a-fA-F]{2})/.exec(text.slice(start + 6, start + 12));
    if (!low)
        return undefined;
    return {
        char: char + String.fromCharCode(parseInt(low[1], 16)),
        length: plain[0].length + low[0].length,
    };
}
/*
  Joiners and modifiers that appear inside a single emoji: variation selectors,
  zero-width joiner, and skin tones. Written as escapes on purpose — every one
  of them is invisible, and a reviewer cannot check a character they cannot see.
*/
const EMOJI_GLUE = /[\u200d\ufe0e\ufe0f]|[\u{1f3fb}-\u{1f3ff}]/gu;
/** The combining mark that makes a keycap emoji out of a plain digit. */
const KEYCAP = "\u20e3";
/**
 * Whether a string is actually a picture, for the decorative `emoji` slots.
 *
 * Worth checking because those slots render at 26px next to a label, so
 * anything that isn't pictographic reads as damage — which is exactly how the
 * `①` bug looked on a real tablet, a truncated `\u2…` sitting where a
 * coloured circle belonged. The field is optional everywhere it appears, so the
 * honest response to "this isn't an emoji" is to drop it.
 */
export function looksLikeEmoji(text) {
    // A keycap is a plain digit wearing a combining mark, so it fails the
    // pictographic test below while being a perfectly good badge for an option.
    if (text.includes(KEYCAP))
        return true;
    const core = text.replace(EMOJI_GLUE, "");
    if (core.length === 0)
        return false;
    for (const char of core) {
        if (!/\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(char))
            return false;
    }
    return true;
}
//# sourceMappingURL=text.js.map